import React, { useState, useEffect, useCallback } from "react";
import { FiUser, FiHome, FiLock, FiSave, FiEye, FiEyeOff } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { API_URL, apiRequest } from '../config/api';

const Settings = () => {
  const { t } = useTranslation();
  const { user, handleAuthError } = useAuth();
  const [activeTab, setActiveTab] = useState('personal');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Personal Info State
  const [personalInfo, setPersonalInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    position: ''
  });

  // Company Info State
  const [companyInfo, setCompanyInfo] = useState({
    companyName: '',
    industry: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    website: '',
    taxId: ''
  });

  // Password Change State
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Fetch settings from backend
  const fetchSettings = useCallback(async () => {
    if (!user?.token) {
      console.log('No authentication token available, skipping fetch');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      console.log('Fetching settings from backend...');
      const response = await apiRequest(`${API_URL}/api/setting`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json',
        },
      }, handleAuthError);

      console.log(`Fetch settings response status: ${response.status}`);

      if (!response.ok) {
        let errorMessage = 'Failed to fetch settings';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
          console.error('Fetch settings error details:', errorData);
        } catch (parseError) {
          console.error('Failed to parse error response:', parseError);
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const fetchedSettings = await response.json();
      console.log('Settings fetched successfully:', fetchedSettings);
      
      // Update local state with fetched settings
      if (fetchedSettings.personalInfo) {
        setPersonalInfo(fetchedSettings.personalInfo);
      }
      if (fetchedSettings.companyInfo) {
        setCompanyInfo(fetchedSettings.companyInfo);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [user?.token, handleAuthError]);

  // Load settings on component mount and when user changes
  useEffect(() => {
    fetchSettings();
  }, [user?.token, fetchSettings]);

  const handlePersonalInfoChange = (field, value) => {
    setPersonalInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCompanyInfoChange = (field, value) => {
    setCompanyInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSavePersonalInfo = async (e) => {
    e.preventDefault();
    
    if (!user?.token) {
      console.error('No authentication token available');
      toast.error(t('settings.alerts.authRequired'));
      return;
    }

    try {
      console.log('Saving personal info...');
      const response = await fetch(`${API_URL}/api/setting/personal`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(personalInfo)
      });

      console.log(`Save personal info response status: ${response.status}`);

      if (!response.ok) {
        let errorMessage = 'Failed to save personal info';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
          console.error('Save personal info error details:', errorData);
        } catch (parseError) {
          console.error('Failed to parse error response:', parseError);
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const updatedPersonalInfo = await response.json();
      console.log('Personal info saved successfully:', updatedPersonalInfo);
      
      // Update local state with saved data
      setPersonalInfo(updatedPersonalInfo);
      toast.success(t('settings.toasts.personalInfoUpdated'));
    } catch (error) {
      console.error('Error saving personal info:', error);
      toast.error(`Failed to save personal info: ${error.message}`);
    }
  };

  const handleSaveCompanyInfo = async (e) => {
    e.preventDefault();
    
    if (!user?.token) {
      console.error('No authentication token available');
      toast.error(t('settings.alerts.authRequired'));
      return;
    }

    try {
      console.log('Saving company info...');
      const response = await fetch(`${API_URL}/api/setting/company`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(companyInfo)
      });

      console.log(`Save company info response status: ${response.status}`);

      if (!response.ok) {
        let errorMessage = 'Failed to save company info';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
          console.error('Save company info error details:', errorData);
        } catch (parseError) {
          console.error('Failed to parse error response:', parseError);
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const updatedCompanyInfo = await response.json();
      console.log('Company info saved successfully:', updatedCompanyInfo);
      
      // Update local state with saved data
      setCompanyInfo(updatedCompanyInfo);
      toast.success(t('settings.toasts.companyInfoUpdated'));
    } catch (error) {
      console.error('Error saving company info:', error);
      toast.error(`Failed to save company info: ${error.message}`);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error(t('settings.toasts.passwordsDoNotMatch'));
      return;
    }

    if (passwordData.newPassword.length < 8) {
      toast.error(t('settings.toasts.passwordTooShort'));
      return;
    }

    if (!user?.token) {
      console.error('No authentication token available');
      toast.error(t('settings.alerts.authRequired'));
      return;
    }

    try {
      console.log('Changing password...');
      const response = await fetch(`${API_URL}/api/setting/password`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        })
      });

      console.log(`Change password response status: ${response.status}`);

      if (!response.ok) {
        let errorMessage = 'Failed to change password';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
          console.error('Change password error details:', errorData);
        } catch (parseError) {
          console.error('Failed to parse error response:', parseError);
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      console.log('Password changed successfully');
      
      // Clear password fields
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      toast.success(t('settings.toasts.passwordChanged'));
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(`Failed to change password: ${error.message}`);
    }
  };

  const tabs = [
    { id: 'personal', name: t('settings.tabs.personal'), icon: FiUser },
    { id: 'company', name: t('settings.tabs.company'), icon: FiHome },
    { id: 'password', name: t('settings.tabs.password'), icon: FiLock }
  ];

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('settings.title')}</h1>
      </div>

      {/* Loading State - skeleton placeholders */}
      {loading && (
        <div className="space-y-6">
          {/* Tabs skeleton */}
          <div className="border-b border-gray-200">
            <div className="flex space-x-8">
              {Array.from({ length: 3 }).map((_, idx) => (
                <div key={idx} className="py-2">
                  <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
                </div>
              ))}
            </div>
          </div>

          {/* Card skeleton */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
            <div className="h-5 w-48 bg-gray-200 rounded mb-6 animate-pulse" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {Array.from({ length: 6 }).map((_, idx) => (
                <div key={idx}>
                  <div className="h-4 w-32 bg-gray-200 rounded mb-2 animate-pulse" />
                  <div className="h-9 w-full bg-gray-100 border border-gray-200 rounded animate-pulse" />
                </div>
              ))}
            </div>
            <div className="mt-6">
              <div className="h-9 w-36 bg-gray-200 rounded animate-pulse" />
            </div>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">{t('settings.error.title')}</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
              <button
                onClick={fetchSettings}
                className="mt-2 text-sm text-red-800 hover:text-red-900 underline"
              >
                {t('settings.error.tryAgain')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tabs and Content - only show when not loading and no error */}
      {!loading && !error && (
        <>
          {/* Tabs */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => {
                const IconComponent = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <IconComponent className="w-4 h-4 mr-2" />
                    {tab.name}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        {activeTab === 'personal' && (
          <div className="p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">{t('settings.personalInfo.title')}</h3>
            <form onSubmit={handleSavePersonalInfo}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('settings.personalInfo.firstName')}
                  </label>
                  <input
                    type="text"
                    value={personalInfo.firstName}
                    onChange={(e) => handlePersonalInfoChange('firstName', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('settings.personalInfo.lastName')}
                  </label>
                  <input
                    type="text"
                    value={personalInfo.lastName}
                    onChange={(e) => handlePersonalInfoChange('lastName', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('settings.personalInfo.email')}
                  </label>
                  <input
                    type="email"
                    value={personalInfo.email}
                    onChange={(e) => handlePersonalInfoChange('email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('settings.personalInfo.phone')}
                  </label>
                  <input
                    type="tel"
                    value={personalInfo.phone}
                    onChange={(e) => handlePersonalInfoChange('phone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('settings.personalInfo.position')}
                  </label>
                  <input
                    type="text"
                    value={personalInfo.position}
                    onChange={(e) => handlePersonalInfoChange('position', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="mt-6">
                <button
                  type="submit"
                  className="flex items-center bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
                >
                  <FiSave className="w-4 h-4 mr-2" />
                  {t('settings.personalInfo.saveChanges')}
                </button>
              </div>
            </form>
          </div>
        )}

        {activeTab === 'company' && (
          <div className="p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">{t('settings.companyInfo.title')}</h3>
            <form onSubmit={handleSaveCompanyInfo}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('settings.companyInfo.companyName')}
                  </label>
                  <input
                    type="text"
                    value={companyInfo.companyName}
                    onChange={(e) => handleCompanyInfoChange('companyName', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('settings.companyInfo.industry')}
                  </label>
                  <input
                    type="text"
                    value={companyInfo.industry}
                    onChange={(e) => handleCompanyInfoChange('industry', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('settings.companyInfo.address')}
                  </label>
                  <input
                    type="text"
                    value={companyInfo.address}
                    onChange={(e) => handleCompanyInfoChange('address', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('settings.companyInfo.city')}
                  </label>
                  <input
                    type="text"
                    value={companyInfo.city}
                    onChange={(e) => handleCompanyInfoChange('city', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('settings.companyInfo.stateProvince')}
                  </label>
                  <input
                    type="text"
                    value={companyInfo.state}
                    onChange={(e) => handleCompanyInfoChange('state', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('settings.companyInfo.zipPostalCode')}
                  </label>
                  <input
                    type="text"
                    value={companyInfo.zipCode}
                    onChange={(e) => handleCompanyInfoChange('zipCode', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('settings.companyInfo.country')}
                  </label>
                  <input
                    type="text"
                    value={companyInfo.country}
                    onChange={(e) => handleCompanyInfoChange('country', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">
                     {t('settings.companyInfo.website')}
                   </label>
                   <input
                     type="text"
                     value={companyInfo.website}
                     onChange={(e) => handleCompanyInfoChange('website', e.target.value)}
                     placeholder="https://www.example.com"
                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                   />
                 </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('settings.companyInfo.taxId')}
                  </label>
                  <input
                    type="text"
                    value={companyInfo.taxId}
                    onChange={(e) => handleCompanyInfoChange('taxId', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="mt-6">
                <button
                  type="submit"
                  className="flex items-center bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
                >
                  <FiSave className="w-4 h-4 mr-2" />
                  {t('settings.companyInfo.saveChanges')}
                </button>
              </div>
            </form>
          </div>
        )}

        {activeTab === 'password' && (
          <div className="p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">{t('settings.changePassword.title')}</h3>
            <form onSubmit={handleChangePassword}>
              <div className="space-y-4 max-w-md">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('settings.changePassword.currentPassword')}
                  </label>
                  <div className="relative">
                    <input
                      type={showCurrentPassword ? "text" : "password"}
                      value={passwordData.currentPassword}
                      onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {showCurrentPassword ? <FiEyeOff className="w-4 h-4 text-gray-400" /> : <FiEye className="w-4 h-4 text-gray-400" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('settings.changePassword.newPassword')}
                  </label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? "text" : "password"}
                      value={passwordData.newPassword}
                      onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {showNewPassword ? <FiEyeOff className="w-4 h-4 text-gray-400" /> : <FiEye className="w-4 h-4 text-gray-400" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('settings.changePassword.confirmNewPassword')}
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={passwordData.confirmPassword}
                      onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {showConfirmPassword ? <FiEyeOff className="w-4 h-4 text-gray-400" /> : <FiEye className="w-4 h-4 text-gray-400" />}
                    </button>
                  </div>
                </div>
              </div>
              <div className="mt-6">
                <button
                  type="submit"
                  className="flex items-center bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
                >
                  <FiLock className="w-4 h-4 mr-2" />
                  {t('settings.changePassword.changePassword')}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
        </>
      )}
    </div>
  );
};

export default Settings; 